import React, { FC, ReactNode } from 'react';
import { QueryResult } from 'react-apollo';
import { FetchMoreOptions } from 'apollo-client';

import { LoaderTypes } from 'types/app';
import { RecommendContentComponent, RecommendedContentResults } from 'api';
import { QueryManager } from 'components';

export interface IRecommendContentOptions {
  onLoadMore: () => void;
  exhausted: boolean;
  loading: boolean;
}

interface IProps {
  size: number;
  resultsFrom: number;
  children: (data: RecommendedContentResults, options: IRecommendContentOptions) => ReactNode;
  loader?: LoaderTypes;
}

const RecommendContent: FC<IProps> = props => {
  const { children, loader, size, resultsFrom } = props;

  const updateQuery: FetchMoreOptions['updateQuery'] = (prev, options) => {
    if (!options.fetchMoreResult) {
      return prev;
    }

    const results = [
      ...prev.recommendContent.results,
      ...options.fetchMoreResult.recommendContent.results,
    ];

    return {
      ...prev,
      recommendContent: { ...options.fetchMoreResult.recommendContent, results },
    };
  };

  function handleLoadMore(queryResult: QueryResult) {
    const { fetchMore, data, loading, error } = queryResult;

    const { recommendContent } = data;
    const { results, results_from, count } = recommendContent;
    const isExhausted = results.length === count;

    if (loading || error || isExhausted) {
      return Promise.resolve();
    }

    return fetchMore({
      updateQuery,
      variables: { results_from: results_from + size },
    });
  }

  return (
    <RecommendContentComponent
      notifyOnNetworkStatusChange
      variables={{ size, results_from: resultsFrom }}>
      {queryResult => (
        <QueryManager loader={loader} {...queryResult}>
          {({ data, loading }) => {
            if (!data?.recommendContent) {
              return null;
            }

            const { results, count } = data.recommendContent;

            return children(data.recommendContent, {
              loading,
              exhausted: count === results.length,
              onLoadMore: () => handleLoadMore(queryResult),
            });
          }}
        </QueryManager>
      )}
    </RecommendContentComponent>
  );
};

export default RecommendContent;
